import { Button } from "primereact/button";
import { useSetDataByView } from "../hooks/useSetDataByView/useSetDataByView";
import { confirmDialog } from "primereact/confirmdialog";
import { useShowToast } from "../contexts/ToastContext.tsx";
import { RowFromView, ViewNames } from "../types";

type RowCancelButtonProps<
  TRow extends { id: number; deliveryStatus?: string; entryStatus?: string },
> = {
  rowData: TRow;
};

export default function RowCancelButton<
  TRow extends { id: number; deliveryStatus?: string; entryStatus?: string },
  TView extends ViewNames,
>({ rowData }: RowCancelButtonProps<TRow>) {
  const { deleteData } = useSetDataByView<TView>();

  const showToast = useShowToast();

  const hasDeliveryStatus = rowData.deliveryStatus !== undefined;
  const canDelete =
    (hasDeliveryStatus && rowData.deliveryStatus === "NEW") ||
    rowData.deliveryStatus === "DRAFT";
  const canCancel = !hasDeliveryStatus && rowData.entryStatus === "default";

  const isCancelled = rowData.entryStatus === "cancelled";
  const isCompleted = rowData.entryStatus === "completed";
  const isPending = rowData.entryStatus === "pending";
  const icon = hasDeliveryStatus ? "pi pi-trash" : "pi pi-times";

  const disabled =
    isCancelled ||
    isCompleted ||
    isPending ||
    (hasDeliveryStatus && !canDelete) ||
    (!hasDeliveryStatus && !canCancel);

  const confirmRemove = () => {
    if (disabled) return;
    console.log("confirmRemove called for rowData:", rowData);
    confirmDialog({
      message: `Czy na pewno chcesz usunąć ten wpis?`,
      header: "Potwierdź usunięcie",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Usuwać",
      rejectLabel: "Anulować",
      accept: () => {
        void (async () => {
          try {
            await deleteData(rowData.id);
            showToast({
              severity: "success",
              summary: "Sukces",
              detail: `Wpis został usunięty`,
            });
          } catch (_error) {
            showToast({
              severity: "error",
              summary: "Błąd",
              detail: "Wpis nie mógł zostać usunięty",
            });
          }
        })();
      },
      acceptClassName: "p-button-danger",
      className: "custom-confirm-dialog",
    });
  };

  return (
    <Button
      severity="danger"
      size="small"
      icon={icon}
      title="Usuwać"
      outlined={!isCancelled}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        confirmRemove();
      }}
      className={
        disabled || !canDelete ? "disabled-cancelled-button" : "p-button-danger"
      }
    />
  );
}

export const RowCancelButtonBodyFunction = <TView extends ViewNames>(
  rowData: RowFromView<TView>,
) => <RowCancelButton<RowFromView<TView>, TView> rowData={rowData} />;
